/**
 * environment.json schema (style guide §3.1-§3.3, §3.6-§3.8, §9.6; spec §4.4 impostors, §8 sky and star).
 * Asset-lane owned (task A8). Directions point TOWARD the light or body (style guide §10.2).
 * Single sources: the star sits on the key light direction, the gas giant on the fill direction
 * (planetshine) and the brightest nebula lobe on the rim direction (nebula back-glow).
 */
import { z } from 'zod'
import type { QualityPreset } from '../contracts'
import environmentJson from './environment.json'
import { MAX_DUST_PATCHES } from './atmosphere'

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)
const vec3 = z.tuple([z.number(), z.number(), z.number()])
const dir = vec3.refine((v) => Math.hypot(v[0], v[1], v[2]) > 0.5, 'direction must be non-zero')
const light = z.object({ color: hex, intensity: z.number().nonnegative(), direction: dir })
const range = z.tuple([z.number(), z.number()]).refine((r) => r[0] <= r[1], 'range must be [min, max]')

function perPreset<T extends z.ZodType>(t: T): z.ZodObject<{ low: T; medium: T; high: T }> {
  return z.object({ low: t, medium: t, high: t })
}

const storm = z.object({
  latDeg: z.number().min(-80).max(80),
  /** Longitude from the meridian that faces the render origin (0 = centre of the visible disc). */
  lonDeg: z.number(),
  radiusDeg: z.number().positive().max(40),
  /** Swirl in radians at the storm centre (sign = spin direction). */
  twist: z.number(),
  color: hex,
  /** 0..1: how much of the storm core is recoloured. */
  tint: z.number().min(0).max(1),
})

/** The painted nebula (sky/nebula.ts). Exported: `systemLooks` regions hold partials of it (R3). */
export const nebulaSchema = z.object({
  /** Three saturated hues: [primary glow, filaments, hot accent]. */
  hues: z.tuple([hex, hex, hex]),
  base: hex,
  brightness: z.number().positive(),
  scale: z.number().positive(),
  warp: z.number().nonnegative(),
  detail: z.number().positive(),
  lobeWidthDeg: z.number().positive().max(120),
  secondLobe: z.number().min(0).max(1),
  bandWidthDeg: z.number().positive().max(60),
  bandGlow: z.number().nonnegative(),
  dust: z.number().min(0).max(1),
  dustScale: z.number().positive(),
  /** Maximum linear radiance written to the bake (8-bit sRGB target: the sky is never HDR). */
  maxRadiance: z.number().positive().max(1),
})

export const environmentFileSchema = z.object({
  exposure: z.number().min(0.5).max(2),
  lookdevSeed: z.number().int().nonnegative(),
  /** scene.environmentIntensity: the hull value of style guide §3.3 (glass sets its own envMap intensity). */
  environmentIntensity: z.number().min(0).max(2),
  lights: z.object({ key: light, fill: light, rim: light }),
  /**
   * Key-light shadow clipmap (shadows.ts): `levels[preset]` concentric light-space squares of `mapSize[preset]`²
   * around the shadow focus; level 0 hugs the focus sphere (× focusScale), each next level is `ratio` × wider.
   */
  shadows: z.object({
    /** Active clipmap levels per preset; 0 = no shadows. */
    levels: perPreset(z.number().int().min(0).max(6)),
    /** Shadow map size of every level per preset; 0 = no shadows. */
    mapSize: perPreset(z.number().int().nonnegative()),
    ratio: z.number().min(2).max(16),
    focusScale: z.number().min(1).max(3),
    /** Smallest level-0 half-extent (metres). */
    minHalfExtent: z.number().positive(),
    /** Cross-fade band at each level's border (fraction of its half-extent). */
    fade: z.number().min(0.01).max(0.5),
    /** How dark key-light shadows get (1 = no key light at all). Below 1 keeps cast shadows near the 9.5:1
     *  key-to-fill ratio instead of black where no fill reaches (style guide §3.1: never black). */
    intensity: z.number().min(0).max(1),
    /** Normal and depth bias in world texels of each level (scales with the level's texel size). */
    normalBiasTexels: z.number().min(0).max(8),
    biasTexels: z.number().min(0).max(8),
    /** Extra depth (metres) toward the star so casters outside a level still cast into it. */
    casterMargin: z.number().nonnegative(),
    /**
     * SC-6a2: the depth (metres) toward the star within which field rocks join the caster sets (PF-2 reach, PF-2b
     * near box). Rocks keep the pre-SC-6a2 400 m so the deeper `casterMargin` (for km stations) does not pull
     * hundreds more rocks into every level; `casterMargin` here restores one margin for everything.
     */
    rockCasterMargin: z.number().nonnegative(),
    /**
     * G5 (PERF, D-TBD): skip zero-weight shadow levels and a level's PCF taps where it does not contribute (a
     * value-identical branch, shadows.ts). False = every allocated level sampled per pixel (pre-G5), for A/B.
     */
    skipZeroLevels: z.boolean().default(true),
    /**
     * SC-6a1: model LODs pick their level once per frame from the view camera, so every shadow level and the AO
     * prepass draw the visible level and no pass resets three's hysteresis (render/viewLod.ts). False = three's
     * per-pass pick from each pass camera (before SC-6a1).
     */
    casterLodFromView: perPreset(z.boolean()),
    /** Focus radius (metres) used until setShadowFocus is called. */
    defaultRadius: z.number().positive(),
  }),
  /**
   * Asteroid LOD switch-ins (asteroids.ts): level l shows from `lodDistances[l] × lodScale[preset] × radius`
   * (per metre of rock radius; PERF PF-3). Below 1 moves every switch closer (fewer triangles); High keeps 1.
   */
  asteroids: z.object({
    lodDistances: z
      .array(z.number().nonnegative())
      .min(1)
      .refine((d) => d[0] === 0, 'LOD0 must switch in at 0')
      .refine((d) => d.every((v, i) => i === 0 || v > d[i - 1]), 'LOD distances must increase'),
    lodScale: perPreset(z.number().positive().max(4)),
  }),
  /** Space haze (post/atmosphere.ts), lit by the key light: depth fog toward the sky colours and field dust. */
  atmosphere: z.object({
    fog: z.object({
      /** Distance (m) where fog starts, density (1/m), maximum opacity, sky blur roughness, colour gain. */
      start: z.number().nonnegative(),
      density: z.number().nonnegative().max(0.01),
      max: z.number().min(0).max(1),
      roughness: z.number().min(0).max(1),
      gain: z.number().nonnegative().max(8),
    }),
    dust: z.object({
      /** Optical depth per patch radius at a patch centre (radius-normalised, so a 2 km or a 150 km field reads alike). */
      opticalDepth: z.number().nonnegative().max(4),
      /** Ceiling on the dust's opacity: the scene always shows through. */
      maxOpacity: z.number().min(0).max(1),
      /** Henyey-Greenstein anisotropy: forward scattering makes the haze glow toward the star. */
      anisotropy: z.number().min(-0.9).max(0.9),
      albedo: z.number().min(0).max(1),
      /** Sky (ambient) share of the dust light. */
      ambient: z.number().min(0).max(4),
      /** Isotropic share of the dust phase function (the rest is the forward lobe). */
      isotropic: z.number().min(0).max(1),
      /** Small pockets of dust scattered through the field's volume, with clear space between them. */
      patches: z.object({
        count: z.number().int().min(1).max(MAX_DUST_PATCHES),
        /** Patch radius range as fractions of the field radius. */
        radius: z.tuple([z.number().positive().max(1), z.number().positive().max(1)]),
        /** How far patch centres scatter from the field centre (fraction of the field radius). */
        spread: z.number().min(0).max(1),
        seed: z.number().int().nonnegative(),
      }),
      /** Wisps: one noise octave breaks each patch's edge (frequency in cells per patch radius, contrast 0..1). */
      wisp: z.object({ scale: z.number().positive(), contrast: z.number().min(0).max(1) }),
      /** How far the dust light leans toward the system's nebula hues (0 = neutral). */
      tint: z.number().min(0).max(1),
    }),
  }),
  impostorDistance: z.number().positive(),
  star: z.object({
    angularRadiusDeg: z.number().positive(),
    color: hex,
    /** E4 band (style guide §3.6): linear luminance 50-200. */
    intensity: z.number().min(50).max(200),
    limbDarkening: z.number().min(0).max(1),
    glare: z.object({
      color: hex,
      intensity: z.number().nonnegative(),
      /** Maximum glare radius as a fraction of the screen height (style guide §9.6: bounded). */
      maxRadius: z.number().positive().max(0.25),
      /** Core width as a fraction of maxRadius. */
      core: z.number().positive().max(1),
    }),
    streak: z.object({
      color: hex,
      intensity: z.number().nonnegative(),
      /** Total streak length as a fraction of the screen width (style guide §3.8: at most 25%). */
      length: z.number().positive().max(0.25),
      /** Streak thickness as a fraction of the screen height. */
      thickness: z.number().positive().max(0.05),
    }),
  }),
  gasGiant: z.object({
    angularRadiusDeg: z.number().positive().max(30),
    /** Spin axis as seen from the render origin: roll from screen-up (degrees, clockwise positive)... */
    axisRollDeg: z.number(),
    /** ...and its lean toward the viewer: 0 = ring seen edge-on, 90 = pole-on. */
    axisOpenDeg: z.number().min(-90).max(90),
    /** Band palette, dark to light. */
    palette: z.tuple([hex, hex, hex, hex, hex]),
    albedo: z.number().min(0.05).max(0.9),
    bands: z.object({
      frequency: z.number().positive(),
      warp: z.number().nonnegative(),
      turbulence: z.number().nonnegative(),
      streaks: z.number().nonnegative(),
      /** Amplitude of the eddies that curl band edges (Kelvin-Helmholtz look), in latitude units. */
      edgeCurl: z.number().nonnegative(),
    }),
    /** Slow band flow: the pattern spins about the axis at this many radians per second. */
    flow: z.object({ speed: z.number().nonnegative() }),
    storms: z.array(storm).max(4),
    terminator: z.object({ wrap: z.number().min(0).max(0.5), color: hex, width: z.number().positive() }),
    night: z.object({ color: hex, intensity: z.number().nonnegative() }),
    atmosphere: z.object({
      color: hex,
      /** Shell thickness as a fraction of the planet radius. */
      thickness: z.number().positive().max(0.2),
      intensity: z.number().nonnegative(),
      rimPower: z.number().positive(),
      falloff: z.number().positive(),
    }),
    ring: z
      .object({
        enabled: z.boolean(),
        /** Radii as multiples of the planet radius. */
        inner: z.number().gt(1),
        outer: z.number().gt(1),
        opacity: z.number().min(0).max(1),
        color: hex,
      })
      .refine((r) => r.outer > r.inner, 'ring outer radius must exceed the inner radius'),
  }),
  sky: z.object({
    /** Radius (metres) of the star sphere; the camera far plane is twice this. */
    radius: z.number().positive(),
    /** Background cubemap face size per preset (spec §8, contract §4.5). */
    bakeSize: perPreset(z.number().int().positive()),
    /** Face size of the cubemap PMREM is built from (reflections). */
    envSize: z.number().int().positive(),
    nebula: nebulaSchema,
    stars: z.object({
      count: z.number().int().positive().max(40000),
      /** Share of stars concentrated in the galactic band. */
      bandShare: z.number().min(0).max(1),
      sizePx: range,
      brightness: range,
      /** Power-law exponent of the brightness distribution (larger = fewer bright stars). */
      powerLaw: z.number().gt(1),
      temperatureK: range,
      /** How strongly dust lanes in the bake dim the stars behind them. */
      dustDimming: z.number().min(0).max(1),
    }),
  }),
  /**
   * Per-system looks for the Reach (M3 R3, `Environment.setSystemLook`); the sandbox never reads this block.
   * A region's nebula is a partial over `sky.nebula`, then seeded variation within `variation` is applied.
   */
  systemLooks: z.object({
    /** Share of the star's class colour in the key light colour (0 = the authored key colour). */
    starColorMix: z.number().min(0).max(1),
    /** Key intensity multiplier by star luminosity: piecewise linear over [luminosity, scale] points, clamped. */
    keyByLuminosity: z
      .array(z.tuple([z.number().positive(), z.number().positive()]))
      .min(1)
      .refine((t) => t.every((p, i) => i === 0 || p[0] > t[i - 1][0]), 'luminosity points must increase'),
    regions: z.object({ gate: nebulaSchema.partial(), mid: nebulaSchema.partial(), deep: nebulaSchema.partial(), rim: nebulaSchema.partial() }),
    variation: z.object({
      /** Hue rotation of all three hues, ± degrees. */
      hueDeg: z.number().min(0).max(60),
      /** Relative brightness change, ± fraction. */
      brightness: z.number().min(0).max(0.5),
      /** Relative warp change, ± fraction. */
      warp: z.number().min(0).max(0.9),
      /** Absolute dust change, ± (clamped to 0..1). */
      dust: z.number().min(0).max(0.5),
    }),
  }),
})

export type EnvironmentFile = z.infer<typeof environmentFileSchema>

export function loadEnvironmentData(): EnvironmentFile {
  return environmentFileSchema.parse(environmentJson)
}

/** Background cubemap face size for a preset. */
export function bakeSizeFor(env: EnvironmentFile, preset: QualityPreset): number {
  return env.sky.bakeSize[preset]
}

/** Key-light shadow map size of every clipmap level for a preset (0 = shadows off). */
export function shadowMapSizeFor(env: EnvironmentFile, preset: QualityPreset): number {
  return env.shadows.mapSize[preset]
}

/** Active key-light shadow clipmap levels for a preset. */
export function shadowLevelsFor(env: EnvironmentFile, preset: QualityPreset): number {
  return env.shadows.mapSize[preset] > 0 ? env.shadows.levels[preset] : 0
}

/** Asteroid LOD switch-in distances per metre of rock radius for a preset (data × the preset's scale). */
export function asteroidLodDistancesFor(env: EnvironmentFile, preset: QualityPreset): number[] {
  const scale = env.asteroids.lodScale[preset]
  return env.asteroids.lodDistances.map((d) => d * scale)
}

export type Vec3Tuple = readonly [number, number, number]

/** Unit vector of a JSON direction. */
export function unitDirection(d: Vec3Tuple): [number, number, number] {
  const l = Math.hypot(d[0], d[1], d[2])
  return [d[0] / l, d[1] / l, d[2] / l]
}
